import React, { useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { compose } from 'redux';
import { Field, Grid, Message, Icon, Popup } from 'nallian-shared-ui/lib/components';
import {
  InputFieldValueComponent,
  TextAreaFieldValueComponent,
  CheckBoxFieldValueComponent,
  ButtonGroupValueComponent,
} from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import { generateErrorsForRequiredFields } from 'nallian-shared-ui/lib/lib/validate';
import { IMPERSONATION_FORM_NAME } from '../../constants';
import { DURATION_KEYS } from 'nallian-shared-ui/lib/impersonation';
import { NoBreakSpan } from '../../../app/styles';
import createImpersonationRequestService from '../../services/createImpersonationRequestService';
import impersonationAllowedDurationsService from '../../services/impersonationAllowedDurationsService';
import { useSharedTranslation } from 'nallian-shared-ui/lib/hooks';

const REQUIRED_FIELDS = ['reason'];

const MAX_REASON_LENGTH = 500;

const EMPTY_REQUEST = {
  ticketReference: '',
  documentAccessEnabled: false,
  reason: '',
  requestedDuration: DURATION_KEYS.P1D,
};

const validate = (values, { t }) => {
  let errors = {};
  errors = generateErrorsForRequiredFields(REQUIRED_FIELDS, values, errors);

  if (values.reason && values.reason.trim().length > MAX_REASON_LENGTH) {
    errors.reason = t('reasonTooLong', { max: MAX_REASON_LENGTH });
  }

  return errors;
};

const ImpersonationRequestModal = ({
  open,
  targetPrincipal,
  onClose,
  dispatchCreateImpersonationRequestPost,
  createImpersonationRequestFetch,
  dispatchImpersonationAllowedDurationsGet,
  impersonationAllowedDurationsFetch,
}) => {
  const { t } = useTranslation();
  const { t: tShared } = useSharedTranslation();

  // Duration options directly from the allowed-durations API
  const durationOptions = useMemo(
    () =>
      (impersonationAllowedDurationsFetch?.value || []).map(d => ({
        value: d,
        key: d,
        text: DURATION_KEYS[d] ? tShared(DURATION_KEYS[d]) : d,
      })),
    [impersonationAllowedDurationsFetch, tShared]
  );

  // Fetch allowed durations on mount
  useEffect(() => {
    if (open) dispatchImpersonationAllowedDurationsGet();
  }, [open, dispatchImpersonationAllowedDurationsGet]);

  // Initial form data - default to P7D if available, otherwise first option
  const data = useMemo(
    () => ({
      ...EMPTY_REQUEST,
      requestedDuration:
        durationOptions.find(d => d.value === 'P7D')?.value ||
        (durationOptions.length > 0 ? durationOptions[0].value : ''),
    }),
    [durationOptions]
  );

  const handleSubmit = useCallback(
    values => {
      const body = {
        targetPrincipalId: targetPrincipal.id,
        requestedDuration: values.requestedDuration,
        ticketReference: values.ticketReference || null,
        documentAccessEnabled: values.documentAccessEnabled || false,
        reason: values.reason.trim(),
      };

      dispatchCreateImpersonationRequestPost(body, onClose, t);
    },
    [targetPrincipal, dispatchCreateImpersonationRequestPost, onClose, t]
  );

  if (!open || !targetPrincipal) return null;

  return (
    <FormModal
      closeIcon
      header={t('impersonationRequest')}
      submitProcessing={createImpersonationRequestFetch?.pending}
      onSubmit={handleSubmit}
      data={data}
      t={t}
      validate={validate}
      form={IMPERSONATION_FORM_NAME}
      enableReinitialize
      cancelRoute=""
      onCancel={onClose}
      onDimmerClick={onClose}
      cancelButtonContent={t('cancel')}
      saveButtonContent={t('submitRequest')}
      modalSize="small"
    >
      <Grid>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            name="requestedDuration"
            label={t('duration')}
            valueComponent={ButtonGroupValueComponent}
            options={durationOptions}
            fluid
            required
          />
        </Grid.Column>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            name="ticketReference"
            label={t('ticketProjectReference')}
            valueComponent={InputFieldValueComponent}
            placeholder={t('ticketProjectReferencePlaceholder')}
            fluid
          />
        </Grid.Column>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            toggle
            name="documentAccessEnabled"
            label={
              <Popup
                compact
                inverted
                trigger={
                  <NoBreakSpan>
                    {t('documentAccess')} <Icon name="info circle" color="grey" />
                  </NoBreakSpan>
                }
                content={t('documentAccessTooltip')}
                position="top left"
              />
            }
            valueComponent={CheckBoxFieldValueComponent}
          />
        </Grid.Column>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            name="reason"
            label={t('reason')}
            valueComponent={TextAreaFieldValueComponent}
            placeholder={t('reasonPlaceholder')}
            rows={4}
            fluid
            required
          />
        </Grid.Column>
      </Grid>
      <Message info>
        <Icon name="info circle" />
        {t('impersonationInfoBanner')}
      </Message>
    </FormModal>
  );
};

ImpersonationRequestModal.propTypes = {
  open: PropTypes.bool.isRequired,
  targetPrincipal: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  dispatchCreateImpersonationRequestPost: PropTypes.func.isRequired,
  createImpersonationRequestFetch: PropTypes.object.isRequired,
  dispatchImpersonationAllowedDurationsGet: PropTypes.func.isRequired,
  impersonationAllowedDurationsFetch: PropTypes.object.isRequired,
};

export default compose(
  createImpersonationRequestService,
  impersonationAllowedDurationsService
)(ImpersonationRequestModal);
